import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  Card,
  Link,
  useTheme
} from '@mui/material';
import {
  Launch as LaunchIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as ViewsIcon,
  Favorite as LikesIcon,
  Comment as CommentsIcon,
  Share as SharesIcon,
  TouchApp as ClicksIcon
} from '@mui/icons-material';
import { getPlatformIcon, getPlatformColor } from '../../utils/platformUtils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

// Platform color scheme
const PLATFORM_COLORS = {
  instagram: '#E4405F',
  twitter: '#1DA1F2',
  facebook: '#1877f2',
  linkedin: '#0077b5',
  tiktok: '#000000',
  youtube: '#FF0000'
};

/**
 * Get engagement rate color based on performance
 */
const getEngagementRateColor = (rate) => {
  if (rate >= 8) return 'success';
  if (rate >= 5) return 'warning';
  if (rate >= 3) return 'info';
  return 'default';
};

/**
 * Truncate text with ellipsis
 */
const truncateText = (text, maxLength = 80) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Format large numbers with abbreviations
 */
const formatNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

/**
 * Top Performing Posts Table Component
 * 
 * Displays a sortable table of top-performing posts with metrics
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Posts data array
 * @param {boolean} props.loading - Loading state
 * @param {number} props.maxHeight - Maximum table height
 * @param {boolean} props.showPagination - Whether to show pagination
 * @param {number} props.pageSize - Number of rows per page
 */
const TopPerformingPostsTable = ({
  data = [],
  loading = false,
  maxHeight = 400,
  showPagination = true,
  pageSize: initialPageSize = 5
}) => {
  const theme = useTheme();
  
  // State management
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);
  const [orderBy, setOrderBy] = useState('engagementRate');
  const [order, setOrder] = useState('desc');

  // Sort and paginate data
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const comparator = (a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      // Handle different data types
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (order === 'desc') {
        return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
      } else {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      }
    };
    
    return [...data].sort(comparator);
  }, [data, order, orderBy]);

  const paginatedData = useMemo(() => {
    if (!showPagination) return sortedData;
    const startIndex = page * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, page, rowsPerPage, showPagination]);

  /**
   * Handle sort request
   */
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  /**
   * Handle page change
   */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  /**
   * Handle rows per page change
   */
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /**
   * Render table header cell with sorting
   */
  const renderSortableHeader = (id, label, numeric = false) => (
    <TableCell
      align={numeric ? 'right' : 'left'}
      sortDirection={orderBy === id ? order : false}
    >
      <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? order : 'asc'}
        onClick={() => handleSort(id)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: maxHeight }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: maxHeight,
        textAlign: 'center',
        p: 3
      }}>
        <TrendingUpIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No posts to display
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your top-performing posts will appear here once you publish content
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer sx={{ maxHeight }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Platform</TableCell>
              <TableCell>Content</TableCell>
              {renderSortableHeader('impressions', 'Views', true)}
              {renderSortableHeader('engagement', 'Engagement', true)}
              {renderSortableHeader('engagementRate', 'Rate', true)}
              {renderSortableHeader('clicks', 'Clicks', true)}
              {renderSortableHeader('publishedAt', 'Published', true)}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((post) => (
              <TableRow 
                key={post.id} 
                hover
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                {/* Platform */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      sx={{
                        bgcolor: PLATFORM_COLORS[post.platform] || theme.palette.grey[500],
                        width: 24,
                        height: 24
                      }}
                    >
                      {React.createElement(getPlatformIcon(post.platform), { fontSize: 'small' })}
                    </Avatar>
                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                      {post.platform}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Content */}
                <TableCell sx={{ maxWidth: 200 }}>
                  <Tooltip title={post.content} arrow>
                    <Typography variant="body2" sx={{ cursor: 'help' }}>
                      {truncateText(post.content, 60)}
                    </Typography>
                  </Tooltip>
                </TableCell>

                {/* Impressions */}
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                    <ViewsIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {formatNumber(post.impressions)}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Engagement */}
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                    <LikesIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {formatNumber(post.engagement)}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Engagement Rate */}
                <TableCell align="right">
                  <Chip
                    label={`${post.engagementRate?.toFixed(1)}%`}
                    color={getEngagementRateColor(post.engagementRate)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>

                {/* Clicks */}
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                    <ClicksIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {post.clicks ? formatNumber(post.clicks) : '-'}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Published At */}
                <TableCell align="right">
                  <Tooltip title={dayjs(post.publishedAt).format('MMM D, YYYY h:mm A')} arrow>
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(post.publishedAt).fromNow()}
                    </Typography>
                  </Tooltip>
                </TableCell>

                {/* Actions */}
                <TableCell align="center">
                  <Tooltip title="View original post" arrow>
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => {
                        // Handle view post action
                        console.log('View post:', post.id);
                      }}
                    >
                      <LaunchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {showPagination && data.length > rowsPerPage && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={sortedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          size="small"
        />
      )}

      {/* Summary */}
      {data.length > 0 && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Performance Summary:
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" fontWeight="medium">
                {data.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Posts
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" fontWeight="medium">
                {(data.reduce((sum, post) => sum + post.engagementRate, 0) / data.length).toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Avg Engagement Rate
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" fontWeight="medium">
                {formatNumber(Math.max(...data.map(post => post.engagement)))}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Best Engagement
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" fontWeight="medium">
                {formatNumber(Math.max(...data.map(post => post.impressions)))}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Best Reach
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TopPerformingPostsTable;